import { Arrays, Errors, isAbsent, isDefined, Option, panic, Progress, RuntimeNotifier, UUID } from "@opendaw/lib-std";
import { network, Promises } from "@opendaw/lib-runtime";
import { ScriptMeta, ScriptPaths, ScriptStorage } from "../scripts";
export class CloudBackupScripts {
    static RemotePath = "scripts";
    static RemoteCatalogPath = `${this.RemotePath}/index.json`;
    static folderFor(uuid) { return `${this.RemotePath}/${uuid}`; }
    static async start(cloudHandler, progress, log, storage = ScriptStorage.get()) {
        log("Collecting all script domains...");
        const [list, cloud] = await Promise.all([
            storage.list(),
            cloudHandler.download(CloudBackupScripts.RemoteCatalogPath)
                .then(json => JSON.parse(new TextDecoder().decode(json)))
                .catch(reason => reason instanceof Errors.FileNotFound ? {} : panic(reason))
        ]);
        const local = list.reduce((record, { uuid, meta }) => {
            record[UUID.toString(uuid)] = meta;
            return record;
        }, {});
        const pristine = await Promise.all(list
            .filter(({ meta }) => isDefined(meta.stock))
            .map(async ({ uuid, meta }) => ScriptStorage.hash(await storage.loadSource(uuid)) === meta.stock
            ? Option.wrap(UUID.toString(uuid)) : Option.None))
            .then(options => new Set(options.filter(option => option.nonEmpty()).map(option => option.unwrap())));
        return new CloudBackupScripts(cloudHandler, storage, { local, cloud }, pristine, log).#start(progress);
    }
    #cloudHandler;
    #storage;
    #scriptDomains;
    #pristine;
    #log;
    constructor(cloudHandler, storage, scriptDomains, pristine, log) {
        this.#cloudHandler = cloudHandler;
        this.#storage = storage;
        this.#scriptDomains = scriptDomains;
        this.#pristine = pristine;
        this.#log = log;
    }
    async #start(progress) {
        const trashed = await this.#storage.loadTrashedIds();
        const [uploadProgress, trashProgress, downloadProgress] = Progress.splitWithWeights(progress, [0.45, 0.10, 0.45]);
        await this.#upload(uploadProgress);
        await this.#trash(trashed, trashProgress);
        await this.#download(trashed, downloadProgress);
    }
    async #upload(progress) {
        const { local, cloud } = this.#scriptDomains;
        const isUnsynced = (localScript, cloudScript) => isAbsent(cloudScript) || CloudBackupScripts.#time(cloudScript) < CloudBackupScripts.#time(localScript);
        const unsynced = CloudBackupScripts.#entries(local)
            .filter(([uuid, meta]) => !this.#pristine.has(uuid) && isUnsynced(meta, cloud[uuid]));
        if (unsynced.length === 0) {
            this.#log("No unsynced scripts found.");
            progress(1.0);
            return;
        }
        const uploaded = await Promises.sequentialAll(unsynced
            .map(([uuid, meta], index, { length }) => async () => {
            progress((index + 1) / length);
            this.#log(`Uploading script '${meta.name}'`);
            const folder = CloudBackupScripts.folderFor(uuid);
            const source = await this.#storage.loadSource(UUID.parse(uuid));
            await Promises.approvedRetry(async () => {
                await this.#cloudHandler.upload(`${folder}/${ScriptPaths.ScriptFile}`, CloudBackupScripts.#encode(source));
                await this.#cloudHandler.upload(`${folder}/${ScriptPaths.ScriptMetaFile}`, CloudBackupScripts.#encode(JSON.stringify(meta)));
            }, error => ({
                headline: "Upload failed",
                message: `Failed to upload script '${meta.name}'. '${error}'`,
                approveText: "Retry",
                cancelText: "Cancel"
            }));
            return [uuid, meta];
        }));
        const catalog = uploaded.reduce((scripts, [uuid, meta]) => {
            scripts[uuid] = meta;
            return scripts;
        }, { ...cloud });
        await this.#uploadCatalog(catalog);
        progress(1.0);
    }
    async #trash(trashed, progress) {
        const { cloud } = this.#scriptDomains;
        const obsolete = CloudBackupScripts.#entries(cloud).filter(([uuid]) => trashed.includes(uuid));
        if (obsolete.length > 0) {
            const approved = await RuntimeNotifier.approve({
                headline: "Delete Scripts?",
                message: `Found ${obsolete.length} locally deleted scripts. Delete from cloud as well?`,
                approveText: "Yes",
                cancelText: "No"
            });
            if (approved) {
                const deleted = await Promises.sequentialAll(obsolete.map(([uuid, meta], index, { length }) => async () => {
                    progress((index + 1) / length);
                    this.#log(`Deleting '${meta.name}'`);
                    await this.#cloudHandler.delete(CloudBackupScripts.folderFor(uuid));
                    return uuid;
                }));
                const catalog = { ...cloud };
                deleted.forEach(uuid => delete catalog[uuid]);
                await this.#uploadCatalog(catalog);
            }
        }
        progress(1.0);
    }
    async #download(trashed, progress) {
        const { cloud, local } = this.#scriptDomains;
        const missing = CloudBackupScripts.#entries(cloud)
            .filter(([uuid]) => isAbsent(local[uuid]) && !trashed.includes(uuid));
        const newer = CloudBackupScripts.#entries(cloud)
            .filter(([uuid, meta]) => {
            const localMeta = local[uuid];
            if (!isDefined(localMeta)) {
                return false;
            }
            return this.#pristine.has(uuid) || CloudBackupScripts.#time(meta) > CloudBackupScripts.#time(localMeta);
        });
        const overriding = newer.length === 0 ? Arrays.empty() : await RuntimeNotifier.approve({
            headline: "Override Scripts?",
            message: `Found ${newer.length} scripts with newer versions in the cloud:\n\n${newer
                .map(([, meta]) => meta.name).join("\n")}\n\nOverride the local versions?`,
            approveText: "Override",
            cancelText: "Keep local"
        }).then(approved => approved ? newer : Arrays.empty());
        const download = [...missing, ...overriding];
        if (download.length === 0) {
            this.#log("No scripts to download.");
            progress(1.0);
            return;
        }
        await Promises.sequentialAll(download.map(([uuid, meta], index, { length }) => async () => {
            progress((index + 1) / length);
            this.#log(`Downloading script '${meta.name}'`);
            const folder = CloudBackupScripts.folderFor(uuid);
            const source = await Promises.guardedRetry(() => this.#cloudHandler.download(`${folder}/${ScriptPaths.ScriptFile}`), network.defaultRetry);
            const metaBytes = await Promises.guardedRetry(() => this.#cloudHandler.download(`${folder}/${ScriptPaths.ScriptMetaFile}`), network.defaultRetry);
            const decoder = new TextDecoder();
            await this.#storage.save(UUID.parse(uuid), ScriptMeta.fromJSON(JSON.parse(decoder.decode(metaBytes))), decoder.decode(source));
        }));
        this.#log("Download scripts complete.");
        progress(1.0);
    }
    async #uploadCatalog(catalog) {
        this.#log("Uploading script catalog...");
        return this.#cloudHandler.upload(CloudBackupScripts.RemoteCatalogPath, CloudBackupScripts.#encode(JSON.stringify(catalog, null, 2)));
    }
    static #entries(scripts) {
        return Object.entries(scripts).map(([uuid, meta]) => [UUID.asString(uuid), meta]);
    }
    static #time(meta) { return new Date(meta.modified).getTime(); }
    static #encode(text) { return new TextEncoder().encode(text).buffer; }
}
